import type React from "react";
import {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  startTransition,
} from "react";
import { useBufferedBounds } from "#app/lib/hooks/use-buffered-bounds";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Box,
  DRAWER_HALF_STOP,
  useIsMobile,
  useMediaQuery,
  toColor,
  useTheme,
} from "@salestrak/ui";

import { MapOverlayControls } from "#app/components/map/map-overlay-controls";
import { useLocationBlockedToast } from "#app/components/map/use-location-blocked-toast";
import { MapInfoboxSheet } from "#app/components/map/map-infobox-sheet";
import { pinsWithPreservedSelection } from "#app/components/map/map-utils";
import type { MapPin } from "#app/components/map/map-view";
import {
  MapApiProvider,
  NativeMapView,
  type Region,
  type MapViewRef,
} from "#app/components/map/map-view";
import {
  DEFAULT_MAP_SETTINGS,
  zoomToLatitudeDelta,
  type MapSettings,
} from "#app/components/map/map-styles";
import {
  useMapInfoboxData,
  usePrefetchMapInfoboxData,
} from "#app/lib/hooks/use-map-infobox-data";
import {
  mapPlaceCoordinateId,
  useRecordMapPlaceVisit,
} from "#app/components/map/use-recent-map-places";
import { useIssueView, useOpenIssueId } from "#app/components/issue-view";
import {
  useMapIssueChanges,
  type MapIssueChange,
} from "#app/components/map/use-map-issue-changes";
import { useAppData } from "#app/lib/data";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useMapClusters } from "#app/lib/hooks/use-map-clusters";
import { getEntityGlyph } from "#app/lib/entity-visuals";
import {
  useUserLocation,
  type UserLocation,
} from "#app/lib/hooks/use-user-location";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import {
  FOCUS_ZOOM,
  parseFocusCoords,
  parseMapRouteSearch,
  pickInitialView,
  regionToView,
  viewToRegion,
  type PersistedMapView,
} from "#app/lib/map";
import { useFilters } from "#app/lib/hooks/use-filters";
import { useTrackFiltersApplied } from "#app/lib/filters/use-track-filters-applied";
import { typingSomewhere } from "#app/lib/utils/typing-target";
import { useIssueFilterConfig } from "#app/lib/hooks/use-issue-filter-config";
import { useCreateMapPin } from "#app/lib/hooks/use-create-map-pin";
import { usePendingPinSearch } from "#app/lib/hooks/use-pending-pin-search";
import { useDetailDrawerWidth } from "#app/components/detail-view/detail-drawer-width";
import type { Omittable } from "@salestrak/ui";
import {
  getShownCardFieldDefs,
  IssueCardProvider,
  useIssueCardEditActions,
  type CardDisplayOptions,
} from "#app/components/issue-card";
import {
  DEFAULT_MAP_CARD_DISPLAY_OPTIONS,
  MapDisplayOptionsPanel,
} from "#app/components/map/map-display-options-panel";
import { useAllCustomFields } from "#app/lib/hooks/use-all-custom-fields";
import { withMemberModule } from "#app/components/permissions/require-permission";
import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";
import { AnalyticsContext, AnalyticsView, MapEvent, trackEvent } from "#app/lib/analytics";

/** How long after the map settles the URL and the remembered view are
 *  written — past the infobox's entrance, well under a reader's next move. */
const VIEW_WRITE_DEBOUNCE_MS = 250;

// True when the where tree already carries an explicit `archived` predicate
// (the "More Options" chip). Walks AND/OR branches so a nested archived
// condition still counts — mirrors the recursive scan the map service runs
// server-side. When false the map injects `archived.eq=false` to hide
// archived issues by default.
function hasArchivedPredicate(
  where: Record<string, unknown> | undefined,
): boolean {
  if (!where) return false;
  if ("archived" in where) return true;
  for (const key of ["AND", "OR"] as const) {
    const branch = where[key];
    if (
      Array.isArray(branch) &&
      branch.some((sub) =>
        hasArchivedPredicate(sub as Record<string, unknown>),
      )
    )
      return true;
  }
  return false;
}

// ─── BufferedMapView ─────────────────────────────────────────────────────
// Owns displayedPins/displayedClusters state so that marker-buffer flushes
// don't re-render the parent MapScreen (which holds infobox state).

type BufferedMapViewProps = Omittable<{
  rawPins: MapPin[];
  rawClusters: ReturnType<typeof useMapClusters>["clusters"];
  selectedPin: MapPin | null;
  onRegionChange: (region: Region) => void;
  isDark: boolean;
  mapSettings: MapSettings;
  isMobile: boolean;
  userLocation: UserLocation;
  onClusterPress: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onPinPress: (pin: MapPin) => void;
  onPinHover: (pin: MapPin) => void;
  onInfoboxClose: () => void;
  onAddProject: (pin: MapPin) => void;
  onMapClick?: (location: { latitude: number; longitude: number }) => void;
  initialRegion?: Region;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<MapViewRef>;
}>;

// Memoized: the screen re-renders for its URL writes, filter state and the
// infobox data, and none of those reach the map surface.
const BufferedMapView = memo(function BufferedMapView({
  rawPins,
  rawClusters,
  selectedPin,
  onRegionChange,
  isDark,
  mapSettings,
  isMobile,
  userLocation,
  onClusterPress,
  onPinPress,
  onPinHover,
  onInfoboxClose,
  onAddProject,
  onMapClick,
  initialRegion,
  ref,
}: BufferedMapViewProps) {
    // ─── Buffered rendering ─────────────────────────────────────────────
    const isMapIdleRef = useRef(true);
    const latestPinsRef = useRef(rawPins);
    const latestClustersRef = useRef(rawClusters);
    latestPinsRef.current = rawPins;
    latestClustersRef.current = rawClusters;

    const [displayedPins, setDisplayedPins] = useState(rawPins);
    const [displayedClusters, setDisplayedClusters] = useState(rawClusters);

    useEffect(() => {
      if (isMapIdleRef.current) {
        startTransition(() => {
          setDisplayedPins(rawPins);
          setDisplayedClusters(rawClusters);
        });
      }
    }, [rawPins, rawClusters]);

    // Keep the selected pin rendered even if the latest clustering result has
    // folded it into a cluster (happens when tapping an edge pin pans the map
    // and the new viewport reclusters its grid cell). Local pins stay out of
    // this collection because their dedicated marker owns the plus treatment.
    const pins: MapPin[] = useMemo(
      () => pinsWithPreservedSelection(displayedPins, selectedPin),
      [displayedPins, selectedPin],
    );

    // The selection is never cleared from here. A data answer (a pan, the
    // drawer's re-centering flight, a re-query) says nothing about whether
    // the user is done with the pin, and the pin stays drawn through
    // `pinsWithPreservedSelection` above. The infobox closes only on an
    // explicit act: its X, Escape, a map click, a search pick, an address
    // edit, or the trashing of its last project (`handleWatchedIssueChange`).

    // ─── Interaction handlers ───────────────────────────────────────────
    const handleInteractionStart = useCallback(() => {
      isMapIdleRef.current = false;
    }, []);

    const onRegionChangeRef = useRef(onRegionChange);
    onRegionChangeRef.current = onRegionChange;

    const handleRegionChange = useCallback((newRegion: Region) => {
      isMapIdleRef.current = true;

      startTransition(() => {
        setDisplayedPins(latestPinsRef.current);
        setDisplayedClusters(latestClustersRef.current);
      });

      onRegionChangeRef.current(newRegion);
    }, []);

    return (
      <NativeMapView
        ref={ref}
        isDark={isDark}
        mapSettings={mapSettings}
        pins={pins}
        clusters={displayedClusters}
        userLocation={userLocation}
        initialRegion={initialRegion}
        onRegionChangeComplete={handleRegionChange}
        onInteractionStart={handleInteractionStart}
        onClusterPress={onClusterPress}
        onPinPress={onPinPress}
        onPinHover={onPinHover}
        selectedPin={selectedPin}
        showInfobox={!isMobile}
        // The mobile infobox sheet opens at the drawer's half stop.
        selectionCover={isMobile ? DRAWER_HALF_STOP : undefined}
        onInfoboxClose={onInfoboxClose}
        onAddProject={onAddProject}
        onMapClick={onMapClick}
      />
    );
});

// ─── MapScreen ───────────────────────────────────────────────────────────

function MapScreen() {
  const navigate = useNavigate();
  const mapRef = useRef<MapViewRef>(null);
  const { workflows, currentUser } = useAppData();
  const { openIssue, openIssueById, openCreateIssue } = useIssueView();
  // The Google Maps style array is JS, not CSS, so it needs the RESOLVED
  // scheme. The library `useTheme()` reports the stored mode ("system" included), so
  // resolve "system" against the OS query here.
  const { theme } = useTheme();
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const isDark = theme === "dark" || (theme === "system" && prefersDark);
  const isMobile = useIsMobile();

  // Filter state
  const filterConfig = useIssueFilterConfig();
  const filters = useFilters(filterConfig);
  useTrackFiltersApplied(filters, AnalyticsView.Map);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [displayOptionsOpen, setDisplayOptionsOpen] = useState(false);

  const { workspace } = Route.useParams();
  const search = Route.useSearch();
  const urlParams = { workspace, ...search };
  const customFieldDefs = useAllCustomFields();
  const { state: storedCardDisplayOptions, update: updateCardDisplayOptions } =
    usePersistedState(
      `map-card-display-v1:${workspace}:${currentUser?.userId ?? "anonymous"}`,
      DEFAULT_MAP_CARD_DISPLAY_OPTIONS,
    );
  // The "Stack fields" switch is hidden from the cards panel (the wrap
  // layout's UX is unsettled), so a locally stored `wrap` reads as stacked
  // the way `parseViewConfig` treats a saved view's.
  const cardDisplayOptions = useMemo<CardDisplayOptions>(
    () => ({ ...storedCardDisplayOptions, fieldLayout: "list" }),
    [storedCardDisplayOptions],
  );
  const visibleCardFieldDefs = useMemo(
    () =>
      getShownCardFieldDefs(
        customFieldDefs,
        cardDisplayOptions.shownFieldIds,
      ),
    [cardDisplayOptions.shownFieldIds, customFieldDefs],
  );

  // Remembered last view — survives nav-away/back. Scoped per workspace so a
  // workspace in one region doesn't bleed its last position into another.
  // Seeded synchronously from localStorage on first render so pickInitialView
  // below sees it without a flicker.
  const { state: lastView, update: setLastView } =
    usePersistedState<PersistedMapView>(
      `map-last-view:${urlParams.workspace ?? ""}`,
      { lat: null, lng: null, z: null },
    );

  const initialRegionRef = useRef<Region | undefined>(undefined);
  if (initialRegionRef.current === undefined) {
    const initial = pickInitialView(urlParams, lastView);
    if (initial) initialRegionRef.current = viewToRegion(initial);
  }

  // Buffered bounds — debounced, with 20% over-fetch and containment check
  const { bounds, zoomLevel, handleRegionChange: handleBoundsRegionChange, reset: resetBounds } = useBufferedBounds();

  // The URL and the remembered view are written on a short debounce, never
  // in the idle frame itself: a router navigation re-renders the whole route,
  // and `idle` lands in the frame where the selection flight parks the pin
  // and the infobox mounts. Nothing reads either that fast.
  const { debounced: writeView } = useDebouncedCallback((r: Region) => {
    const view = regionToView(r);
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        lat: view.lat,
        lng: view.lng,
        z: view.z,
      }),
      replace: true,
    });
    // Mirror to localStorage so position survives nav-away/back even
    // when the inbound URL has no params (e.g. clicking Map in the
    // sidebar).
    setLastView(view);
  }, VIEW_WRITE_DEBOUNCE_MS);
  const handleRegionChange = useCallback(
    (r: Region) => {
      handleBoundsRegionChange(r);
      writeView(r);
    },
    [handleBoundsRegionChange, writeView],
  );

  const { state: mapSettings, update: setMapSettings } = usePersistedState(
    "map-settings",
    DEFAULT_MAP_SETTINGS,
  );

  const {
    location: userLocation,
    error: locationError,
    requestPermission,
  } = useUserLocation(mapSettings.accuracySensors);

  // Selected pin for infobox
  const [selectedPin, setSelectedPin] = useState<MapPin | null>(null);
  // The infobox sheet is a Drawer whose presence IS `selectedPin != null` —
  // there is no imperative present()/dismiss() handle.
  const updateSelectedPin = useCallback(
    (
      updater:
        | MapPin
        | null
        | ((prev: MapPin | null) => MapPin | null),
    ) => {
      setSelectedPin((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    [],
  );
  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle();

  // Wrap issue filters for the MapClusterWhereInput shape
  const mapWhere = useMemo(() => {
    const issueConditions: Record<string, unknown>[] = [];
    if (filters.where) issueConditions.push(filters.where);
    if (isCurrentUserFilterApplied)
      issueConditions.push({ onlyCurrentUser: true });
    // Default the map to active-only. Unless the user has explicitly set an
    // archived filter via the "More Options" chip (Show only archived /
    // Include archived), exclude archived issues so they don't show by
    // default.
    if (!hasArchivedPredicate(filters.where))
      issueConditions.push({ archived: { eq: false } });
    if (issueConditions.length === 0) return undefined;
    if (issueConditions.length === 1) return { issue: issueConditions[0] };
    return { issue: { AND: issueConditions } };
  }, [filters.where, isCurrentUserFilterApplied]);

  // Collect entity IDs that must not be swallowed by clusters.
  // Serialize to a stable key so the array ref doesn't change when
  // selectedPin is replaced by a new object with the same issueIds.
  const preserveIdsKey = selectedPin && !selectedPin.isLocal && selectedPin.issueIds.length > 0
    ? selectedPin.issueIds.join(",")
    : "";
  const preserveIds = useMemo(
    () => (preserveIdsKey ? preserveIdsKey.split(",") : undefined),
    [preserveIdsKey],
  );

  // Live data from GraphQL (with filter where clause)
  const {
    pins: rawPins,
    clusters: rawClusters,
    refresh: refreshMap,
  } = useMapClusters(bounds, zoomLevel, mapWhere, preserveIds);
  // One edit from the map card reaches the map twice: the card's own
  // onIssueChanged, then the cache watcher below when the committed write
  // lands. Both ask for the same re-query, so they share one.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scheduleRefreshMap = useCallback(() => {
    if (refreshTimerRef.current !== undefined) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = undefined;
      refreshMap();
    }, 0);
  }, [refreshMap]);
  useEffect(() => () => clearTimeout(refreshTimerRef.current), []);

  // Resolve Google first, then publish one pin at the canonical address.
  const { createPin, cancel: cancelCreatePin } = useCreateMapPin();

  // Fetch infobox data at screen level (the Drawer portals outside IssueViewProvider)
  const {
    issues: infoboxIssues,
    addressDetails: infoboxAddress,
    propertyDetailsLoading,
  } = useMapInfoboxData(selectedPin);

  // An open infobox is a visit for the Map menu's Recent rows (a project
  // search pick is the other door). An ADDRESS pick is recorded only here:
  // the focus finds-or-creates a pin whose canonical address can differ from
  // the resolved suggestion, and recording both listed two rows for one
  // pick. The entry waits for the infobox data so it can carry a name: a pin
  // holding ONE project is that project (the same entry a project pick
  // writes, so the two doors dedupe); any other pin — several projects, or a
  // bare address — is the address. Recorded once per selected pin: the data
  // refetches while the pin stays open, and re-writing would only churn the
  // timestamp. The infobox data is read by ID, never by arrival: the hook
  // keeps the PREVIOUS pin's issues and address on screen until the new
  // pin's land, so a write on the first render after a pin change would
  // file the new pin under the old pin's name.
  const recordMapPlaceVisit = useRecordMapPlaceVisit();
  const recordedPinIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedPin) {
      recordedPinIdRef.current = null;
      return;
    }
    if (recordedPinIdRef.current === selectedPin.id) return;
    const { latitude: lat, longitude: lng, addressId } = selectedPin;
    if (selectedPin.issueIds.length === 1) {
      const issueId = selectedPin.issueIds[0];
      const issue = infoboxIssues.find((row) => row.issueId === issueId);
      if (!issue) return;
      recordedPinIdRef.current = selectedPin.id;
      recordMapPlaceVisit({
        kind: "issue",
        id: issue.issueId,
        name: issue.name,
        lat,
        lng,
        addressId: addressId ?? issue.addressId,
        workflowId: issue.workflowId,
        iconName: issue.issueType?.icon?.icon ?? issue.icon?.icon ?? null,
        // Types are non-themed: their glyph carries no color, so only the
        // project's own icon (the fallback) brings one.
        iconColor: issue.issueType?.icon?.icon ? null : (issue.icon?.color ?? null),
      });
      return;
    }
    const wholeAddress =
      selectedPin.wholeAddress ??
      (addressId && infoboxAddress?.addressId === addressId
        ? infoboxAddress.wholeAddress
        : undefined);
    if (!wholeAddress) return;
    recordedPinIdRef.current = selectedPin.id;
    recordMapPlaceVisit({
      kind: "address",
      id: addressId ?? mapPlaceCoordinateId(lat, lng),
      name: wholeAddress,
      lat,
      lng,
      addressId,
    });
  }, [infoboxAddress, infoboxIssues, recordMapPlaceVisit, selectedPin]);

  // Once GraphQL delivers a server pin containing the same issues as the
  // local pin, swap selectedPin to the server version so the local-pin
  // render branch stops and the cleanup effect can manage it normally.
  useEffect(() => {
    if (!selectedPin?.isLocal || selectedPin.issueIds.length === 0) return;
    const serverPin = rawPins.find((p) =>
      selectedPin.issueIds.some((id) => p.issueIds.includes(id)),
    );
    if (serverPin) updateSelectedPin(serverPin);
  }, [rawPins, selectedPin, updateSelectedPin]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleZoomIn = useCallback(() => {
    mapRef.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.zoomOut();
  }, []);

  const centerOnLocation = useCallback(
    (fix: { latitude: number; longitude: number }) => {
      const targetZoom = 16;
      if (zoomLevel < targetZoom - 1) {
        mapRef.current?.animateToRegion(
          {
            latitude: fix.latitude,
            longitude: fix.longitude,
            latitudeDelta: zoomToLatitudeDelta(targetZoom),
            longitudeDelta: zoomToLatitudeDelta(targetZoom) * 1.5,
          },
          500,
        );
      } else {
        mapRef.current?.panTo({
          latitude: fix.latitude,
          longitude: fix.longitude,
        });
      }
    },
    [zoomLevel],
  );

  // Without a fix, a press (the C shortcut, or the toast's Try again) re-asks
  // the browser. On a denied site that fails at once with no prompt, so the
  // failure is said out loud through the toast even if it was dismissed —
  // the press asked for it. A fresh grant centers straight from the result
  // rather than waiting for the state round-trip.
  const retryLocationRef = useRef<() => void>(() => {});
  const { show: showLocationBlockedToast } = useLocationBlockedToast({
    error: locationError,
    onRetry: () => retryLocationRef.current(),
  });
  const handleLocationPress = useCallback(async () => {
    trackEvent({ context: AnalyticsContext.Map, event: MapEvent.Locate });
    if (userLocation) {
      centerOnLocation(userLocation);
      return;
    }
    const result = await requestPermission();
    if (result.location) centerOnLocation(result.location);
    else showLocationBlockedToast();
  }, [
    userLocation,
    requestPermission,
    centerOnLocation,
    showLocationBlockedToast,
  ]);
  retryLocationRef.current = () => void handleLocationPress();

  const drawerWidth = useDetailDrawerWidth();

  // Each navigation ends in a selection at the location (the pin search
  // picks a match or creates a pin), so the camera lands where that
  // selection parks the pin: a far jump snaps there, with no second pan.
  const panTo = useCallback(
    (location: { latitude: number; longitude: number }) => {
      const zoom = 16;
      mapRef.current?.animateToRegion(
        {
          latitude: location.latitude,
          longitude: location.longitude,
          latitudeDelta: zoomToLatitudeDelta(zoom),
          longitudeDelta: zoomToLatitudeDelta(zoom) * 1.5,
        },
        500,
        { offsetX: drawerWidth },
      );
    },
    [drawerWidth],
  );

  const { start: startPinSearch, cancel: cancelPinSearch } = usePendingPinSearch({
    pins: rawPins,
    onMatch: (match) => updateSelectedPin(match),
    onFallback: (coords) => {
      updateSelectedPin(null);
      void createPin(coords.lat, coords.lng).then((pin) => {
        if (pin) updateSelectedPin(pin);
      });
    },
    where: mapWhere,
  });

  const navigateToLocation = useCallback(
    (coords: { latitude: number; longitude: number }, addressId?: string) => {
      panTo(coords);
      updateSelectedPin(null);
      cancelCreatePin();
      resetBounds();
      const zoom = FOCUS_ZOOM;
      handleRegionChange({
        latitude: coords.latitude,
        longitude: coords.longitude,
        latitudeDelta: zoomToLatitudeDelta(zoom),
        longitudeDelta: zoomToLatitudeDelta(zoom) * 1.5,
      });
      startPinSearch({
        lat: coords.latitude,
        lng: coords.longitude,
        addressId,
      });
    },
    [panTo, cancelCreatePin, handleRegionChange, resetBounds, startPinSearch, updateSelectedPin],
  );

  // One-shot focus from an external navigation (e.g. selecting an address in
  // global search): pan to the coords and find-or-create a pin there, popping
  // the infobox.
  //
  // Depends ONLY on the focus params — the two callbacks go through refs.
  // navigateToLocation's identity churns (it closes over handleRegionChange,
  // which closes over usePersistedState's setter), and it WRITES the URL, so
  // with it in the dep array every run scheduled the next one. The params
  // themselves change exactly twice — set by the inbound navigation, cleared
  // below — which is the real "one shot", so no latch ref is needed to enforce
  // it. An earlier version used a ref latch instead and had to choose between
  // looping (re-arm on teardown) and silently skipping the pin search when
  // StrictMode's mount/teardown/replay cancelled it (never re-arm). Narrowing
  // the deps removes the choice.
  const navigateToLocationRef = useRef(navigateToLocation);
  navigateToLocationRef.current = navigateToLocation;
  const focusNavigateRef = useRef(navigate);
  focusNavigateRef.current = navigate;
  // Primitive deps, not the params object: the object is a fresh reference
  // per render, and only these three members arm the one-shot focus.
  const { focusLat, focusLng, addressId: focusAddressId } = urlParams;
  useEffect(() => {
    // Parsed by the same helper that seeds the initial view, so a blank pair
    // (`?focusLat=&focusLng=`) can't mean "no focus" to pickInitialView while
    // meaning the coordinate (0, 0) here.
    const focus = parseFocusCoords({ focusLat, focusLng });
    if (!focus) return;
    // Safe to repeat: navigateToLocation cancels any in-flight pin search before
    // starting a new one, so a StrictMode replay just redoes the lookup.
    navigateToLocationRef.current(
      { latitude: focus.lat, longitude: focus.lng },
      focusAddressId,
    );
    focusNavigateRef.current({
      to: ".",
      search: (prev) => ({
        ...prev,
        focusLat: undefined,
        focusLng: undefined,
        addressId: undefined,
      }),
      replace: true,
    });
  }, [focusLat, focusLng, focusAddressId]);

  // URL → selected pin. When the address-button on an issue dialog pushes
  // /map?issueId=…, seed a local pin so the infobox opens immediately. The
  // existing local-pin → server-pin swap effect upgrades it to the real
  // clustered pin when the map query resolves. Re-seeds when issueId
  // changes; ignores lat/lng churn from handleRegionChange's URL writes.
  const handledIssueIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const id = urlParams.issueId;
    if (!id || handledIssueIdRef.current === id) return;
    const lat = urlParams.lat;
    const lng = urlParams.lng;
    if (lat === undefined || lng === undefined) return;
    handledIssueIdRef.current = id;
    updateSelectedPin({
      id: urlParams.addressId ?? `pin-${lat},${lng}`,
      latitude: lat,
      longitude: lng,
      color: "grey",
      count: 1,
      addressId: urlParams.addressId,
      issueIds: [id],
      isLocal: true,
    });
    // Seed params are one-shot — clear them so a refresh doesn't replay the
    // selection. lat/lng/z stay (handleRegionChange keeps them in sync with
    // the camera for shareable URLs).
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, issueId: undefined, addressId: undefined }),
      replace: true,
    });
  }, [urlParams.issueId, urlParams.lat, urlParams.lng, urlParams.addressId, updateSelectedPin, navigate]);

  // The invariant: camera = pin shifted left by drawerWidth/2. One effect,
  // two inputs. Drawer opens/closes, drawer drag-resizes, selected pin
  // changes — all funnel through here. MapView's ref queue handles the
  // case where the underlying Google Map isn't ready yet.
  useEffect(() => {
    if (!selectedPin) return;
    mapRef.current?.focusWithOffset(
      { latitude: selectedPin.latitude, longitude: selectedPin.longitude },
      drawerWidth,
    );
  }, [drawerWidth, selectedPin]);

  const handleClusterPress = useCallback(
    (clusterBounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    }) => {
      mapRef.current?.fitToCoordinates(
        [
          { latitude: clusterBounds.north, longitude: clusterBounds.east },
          { latitude: clusterBounds.south, longitude: clusterBounds.west },
        ],
        {
          edgePadding: { top: 50, right: 50, bottom: 50, left: 50 },
          animated: true,
        },
      );
    },
    [],
  );

  const selectedPinRef = useRef(selectedPin);
  selectedPinRef.current = selectedPin;
  const rawPinsRef = useRef(rawPins);
  rawPinsRef.current = rawPins;

  // A pointer resting on a pin warms the infobox's queries, so the click
  // opens on the settled cards and address, never on skeletons the answers
  // then reshape.
  const handlePinHover = usePrefetchMapInfoboxData();

  // The (selectedPin, drawerWidth) effect owns camera positioning.
  const handlePinPress = useCallback((pin: MapPin) => {
    trackEvent({
      context: AnalyticsContext.Map,
      event: MapEvent.OpenPin,
      data: { kind: "existing", projectCount: pin.count },
    });
    cancelPinSearch();
    cancelCreatePin();
    updateSelectedPin(pin);
  }, [cancelCreatePin, cancelPinSearch, updateSelectedPin]);

  const handleInfoboxClose = useCallback(() => {
    updateSelectedPin(null);
    cancelCreatePin();
  }, [cancelCreatePin, updateSelectedPin]);

  // Screen-scoped keys. Unlike zoom/pan (Google's own bindings, live only
  // while the canvas has focus) these are app-registered and work anywhere on
  // the map, so they must yield to typing and to a keystroke something else
  // already claimed (an open menu or dialog inside the infobox, the
  // G-then-letter module chord — both preventDefault before this runs).
  //
  // - Bare C centers the map on the viewer, the chord the locate dock action
  //   advertises.
  // - Escape drops the selected pin, the same exit as the infobox's close
  //   button. Desktop only: on mobile the infobox is a Drawer that already
  //   owns Escape.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.defaultPrevented || typingSomewhere(e.target)) return;
      if (e.key === "Escape") {
        if (isMobile || selectedPin == null) return;
        e.preventDefault();
        handleInfoboxClose();
        return;
      }
      // `e.key`, not `e.code`: with no modifier held the key IS the
      // character, so the binding follows the viewer's layout.
      if (e.key.toUpperCase() !== "C") return;
      e.preventDefault();
      void handleLocationPress();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleInfoboxClose, handleLocationPress, isMobile, selectedPin]);


  // Every point on the map is an address, so a bare-map click always SCOUTS
  // the house under the pointer — with a pin open as much as with none. A
  // rep working a street moves house to house in one click each; the open
  // infobox is replaced, never a gate to click through first. Closing is the
  // header's X or Escape, never the ground (Brennon, 2026-09-15). Markers are
  // hit-tested by Google and never reach here, so pin-to-pin is one click too.
  const handleMapClick = useCallback(
    (location: { latitude: number; longitude: number }) => {
      cancelPinSearch();
      updateSelectedPin(null);

      void createPin(
        location.latitude,
        location.longitude,
      ).then((pin) => {
        if (!pin) return;
        // reverseGeocode snapped the click to the address's canonical coords.
        // If a server pin already exists for that address, open it instead of
        // dropping an empty local pin on top of it.
        const existing = rawPinsRef.current.find(
          (p) =>
            (pin.addressId && p.addressId === pin.addressId) ||
            (p.latitude === pin.latitude && p.longitude === pin.longitude),
        );
        trackEvent({
          context: AnalyticsContext.Map,
          event: MapEvent.OpenPin,
          data: existing
            ? { kind: "existing", projectCount: existing.count, via: "map_click" }
            : { kind: "scouted", projectCount: 0, via: "map_click" },
        });
        updateSelectedPin(existing ?? pin);
      });
    },
    [cancelPinSearch, createPin, updateSelectedPin],
  );

  const handleAddProject = useCallback(
    (pin: MapPin) => {
      const ownerName = pin.propertyDetails?.ownerNames?.[0];
      openCreateIssue({
        source: "map",
        address: pin.wholeAddress
          ? { wholeAddress: pin.wholeAddress, latLng: { lat: pin.latitude, lng: pin.longitude } }
          : undefined,
        name: ownerName || pin.wholeAddress || undefined,
        onCreated: (issue) => {
          const colorKey = toColor(issue.icon?.color) ?? "grey";
          const workflow = workflows.find(
            (w) => w.workflowId === issue.workflowId,
          );
          // Pins without a custom workflow icon stay glyph-less dots — only
          // resolve through entity-visuals when there is an icon to show.
          const workflowIcon = workflow?.icon?.icon
            ? (getEntityGlyph({
                kind: "workflow",
                id: workflow.workflowId,
                workflow,
              }).icon ?? undefined)
            : undefined;
          updateSelectedPin((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              id: issue.issueId,
              count: 1,
              color: colorKey,
              icon: workflowIcon,
              issueIds: [...prev.issueIds, issue.issueId],
            };
          });
        },
      });
    },
    [openCreateIssue, workflows, updateSelectedPin],
  );

  const handleIssuePress = useCallback(
    (issue: Parameters<typeof openIssue>[0]) => openIssue(issue, { source: "map" }),
    [openIssue],
  );

  const handleSheetAddProject = useCallback(() => {
    if (selectedPinRef.current) handleAddProject(selectedPinRef.current);
  }, [handleAddProject]);

  // The open project rides in the URL (`?issue=`) so a reload, Back or a
  // copied link reopens it. URL → panel once per id: the panel's own write
  // below lands here too, and finds the project already open. Panel → URL:
  // the URL follows the open project and drops it on close — but only once a
  // project has been open, so a deep link is not cleared while it loads.
  const openIssueId = useOpenIssueId();
  const urlIssue = urlParams.issue;
  const handledUrlIssueRef = useRef<string | undefined>(undefined);
  // Only the id arms the open; the panel is read as it stands at that moment.
  const openIssueIdRef = useRef(openIssueId);
  openIssueIdRef.current = openIssueId;
  const openIssueByIdRef = useRef(openIssueById);
  openIssueByIdRef.current = openIssueById;
  useEffect(() => {
    if (!urlIssue || handledUrlIssueRef.current === urlIssue) return;
    handledUrlIssueRef.current = urlIssue;
    if (openIssueIdRef.current !== urlIssue) {
      openIssueByIdRef.current(urlIssue, { source: "deep_link" });
    }
  }, [urlIssue]);
  const panelWasOpenRef = useRef(false);
  useEffect(() => {
    if (openIssueId) panelWasOpenRef.current = true;
    else if (!panelWasOpenRef.current) return;
    if ((openIssueId ?? undefined) === urlIssue) return;
    handledUrlIssueRef.current = openIssueId ?? undefined;
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, issue: openIssueId ?? undefined }),
      replace: true,
    });
  }, [navigate, openIssueId, urlIssue]);

  // The map query is `no-cache`, so a change made anywhere but the map card
  // (the Project panel, another session) never reached the pins.
  // Watch the projects the map is showing — the open pin's and the panel's —
  // and re-ask on a pin-relevant change. A trashed project also leaves the
  // open pin; the card closes when it was the pin's last project, rather than
  // waiting on a card that will never load.
  const watchedIssueIds = useMemo(() => {
    const ids = selectedPin ? [...selectedPin.issueIds] : [];
    if (openIssueId) ids.push(openIssueId);
    return ids;
  }, [selectedPin, openIssueId]);
  const handleWatchedIssueChange = useCallback(
    ({ issueId, removed }: MapIssueChange) => {
      scheduleRefreshMap();
      if (!removed) return;
      updateSelectedPin((prev) => {
        if (!prev?.issueIds.includes(issueId)) return prev;
        const issueIds = prev.issueIds.filter((id) => id !== issueId);
        return issueIds.length > 0
          ? { ...prev, issueIds, count: issueIds.length }
          : null;
      });
    },
    [scheduleRefreshMap, updateSelectedPin],
  );
  useMapIssueChanges(watchedIssueIds, handleWatchedIssueChange);

  const issueCardActions = useIssueCardEditActions({
    onAddressChanged: handleInfoboxClose,
    onIssueChanged: scheduleRefreshMap,
    view: AnalyticsView.Map,
  });

  // Stable, so the memoized overlay controls skip the screen's re-renders.
  const handleFilterPanelOpenChange = useCallback((open: boolean) => {
    // One popover over the map at a time.
    if (open) setDisplayOptionsOpen(false);
    setFilterPanelOpen(open);
  }, []);
  const handleDisplayOptionsClick = useCallback(() => {
    setFilterPanelOpen(false);
    setDisplayOptionsOpen((open) => !open);
  }, []);

  return (
    <>
      {/* Column, not a bare Box: the map surface below claims its height with
          flex-1, which needs a flex parent to grow inside. A block wrapper
          leaves it at content height — i.e. zero, since the map paints into an
          empty div. */}
      <IssueCardProvider
        actions={issueCardActions}
        customFieldDefs={visibleCardFieldDefs}
        displayOptions={cardDisplayOptions}
      >
        <Box direction="column" fill="flex" position="relative">
          <BufferedMapView
          ref={mapRef}
          rawPins={rawPins}
          rawClusters={rawClusters}
          selectedPin={selectedPin}
          onRegionChange={handleRegionChange}
          isDark={isDark}
          mapSettings={mapSettings}
          isMobile={isMobile}
          userLocation={userLocation}
          initialRegion={initialRegionRef.current}
          onClusterPress={handleClusterPress}
          onPinPress={handlePinPress}
          onPinHover={handlePinHover}
          onInfoboxClose={handleInfoboxClose}
          onAddProject={handleAddProject}
          onMapClick={handleMapClick}
          />

          <MapOverlayControls
          mapShade={mapSettings.mapShade}
          rightInset={drawerWidth}
          currentUserSelected={isCurrentUserButtonSelected}
          onCurrentUserClick={handleCurrentUserPress}
          onCurrentUserClear={handleCurrentUserClear}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          zoomLevel={zoomLevel}
          onLocationClick={userLocation ? handleLocationPress : undefined}
          onSearchLocationSelect={navigateToLocation}
          onSearchProjectSelect={openIssueById}
          filterConfig={filterConfig}
          filters={filters}
          filterPanelOpen={filterPanelOpen}
          onFilterPanelOpenChange={handleFilterPanelOpenChange}
          displayOptionsOpen={displayOptionsOpen}
          onDisplayOptionsClick={handleDisplayOptionsClick}
          />

          {isMobile && (
            <MapInfoboxSheet
            pin={selectedPin}
            issues={infoboxIssues}
            addressDetails={infoboxAddress}
            propertyDetailsLoading={propertyDetailsLoading}
            showPropertyFacts={mapSettings.propertyFacts}
            onDismiss={handleInfoboxClose}
            onIssueClick={handleIssuePress}
            onAddProject={handleSheetAddProject}
            />
          )}
        </Box>
      </IssueCardProvider>

      <MapDisplayOptionsPanel
        cardOptions={cardDisplayOptions}
        fields={customFieldDefs}
        mapSettings={mapSettings}
        onCardOptionsChange={updateCardDisplayOptions}
        onMapSettingsChange={setMapSettings}
        onOpenChange={setDisplayOptionsOpen}
        open={displayOptionsOpen}
      />
    </>
  );
}

// The Maps API context sits above the screen: vis.gl's provider builds a new
// context value on each render, so inside the screen every marker re-rendered
// with each URL write and filter change.
function MapRouteScreen() {
  return (
    <MapApiProvider>
      <MapScreen />
    </MapApiProvider>
  );
}

export const Route = createFileRoute("/(app)/$workspace/map")({
  validateSearch: parseMapRouteSearch,
  // A member module: a Guest never reaches it (`ModuleTier`). The rail
  // omits the tile; the route denies for itself since the URL still resolves.
  component: withMemberModule(MapRouteScreen),
});
